import { useMemo, useState } from "react";
import { useTranslation } from "../i18n";
import type { UsageEvent } from "../types/usage";
import { Button } from "./ui/button";

interface UsageChartProps {
  prefix: "admin" | "user";
  events: UsageEvent[];
  isLoading?: boolean;
  rangeDays: number;
  onRangeDaysChange: (days: number) => void;
}

export const UsageChart = ({
  prefix,
  events,
  isLoading = false,
  rangeDays,
  onRangeDaysChange,
}: UsageChartProps) => {
  const { t } = useTranslation();
  const [metric, setMetric] = useState<"cost" | "tokens">("cost");

  const chartSeries = useMemo(() => {
    const end = new Date();
    const start = new Date(end.getTime() - rangeDays * 24 * 60 * 60 * 1000);
    const dayKeys: string[] = [];
    for (let i = 0; i <= rangeDays; i += 1) {
      const d = new Date(start.getTime() + i * 24 * 60 * 60 * 1000);
      dayKeys.push(d.toISOString().slice(0, 10));
    }
    const buckets: Record<string, number> = {};
    dayKeys.forEach((k) => (buckets[k] = 0));
    events.forEach((event) => {
      const key = new Date(event.created_at * 1000).toISOString().slice(0, 10);
      if (!(key in buckets)) return;
      if (metric === "tokens") {
        buckets[key] += event.total_tokens || 0;
      } else {
        buckets[key] += event.cost_usd || 0;
      }
    });
    return dayKeys.map((key) => ({
      key,
      value: buckets[key] || 0,
    }));
  }, [events, metric, rangeDays]);

  const totals = useMemo(() => {
    let totalCost = 0;
    let totalTokens = 0;
    events.forEach((event) => {
      totalCost += event.cost_usd || 0;
      totalTokens += event.total_tokens || 0;
    });
    return { totalCost, totalTokens };
  }, [events]);

  const chartPoints = useMemo(() => {
    if (!chartSeries.length) return "";
    const values = chartSeries.map((d) => d.value);
    const max = Math.max(...values, 1);
    const width = 600;
    const height = 160;
    return chartSeries
      .map((d, idx) => {
        const x = (idx / (chartSeries.length - 1 || 1)) * width;
        const y = height - (d.value / max) * (height - 20) - 10;
        return `${x.toFixed(2)},${y.toFixed(2)}`;
      })
      .join(" ");
  }, [chartSeries]);

  const chartArea = useMemo(() => {
    if (!chartPoints) return "";
    return `0,160 ${chartPoints} 600,160`;
  }, [chartPoints]);

  return (
    <div className="border-t pt-4 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-sm font-medium">
            {t(`${prefix}.usage_chart_title`, "Usage over time")}
          </div>
          <div className="text-xs text-muted-foreground">
            {t(`${prefix}.usage_chart_hint`, "Daily totals")}
          </div>
        </div>
        {isLoading && (
          <div className="text-xs text-muted-foreground">
            {t(`${prefix}.loading_usage`, "Loading...")}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant={metric === "cost" ? "default" : "outline"}
          onClick={() => setMetric("cost")}
        >
          {t(`${prefix}.metric_cost`, "Cost")}
        </Button>
        <Button
          size="sm"
          variant={metric === "tokens" ? "default" : "outline"}
          onClick={() => setMetric("tokens")}
        >
          {t(`${prefix}.metric_tokens`, "Tokens")}
        </Button>
        <div className="flex-1" />
        <Button
          size="sm"
          variant={rangeDays === 7 ? "default" : "outline"}
          onClick={() => onRangeDaysChange(7)}
        >
          {t(`${prefix}.range_7d`, "7d")}
        </Button>
        <Button
          size="sm"
          variant={rangeDays === 30 ? "default" : "outline"}
          onClick={() => onRangeDaysChange(30)}
        >
          {t(`${prefix}.range_30d`, "30d")}
        </Button>
        <Button
          size="sm"
          variant={rangeDays === 90 ? "default" : "outline"}
          onClick={() => onRangeDaysChange(90)}
        >
          {t(`${prefix}.range_90d`, "90d")}
        </Button>
      </div>
      <div className="rounded-lg border bg-background/60 p-3">
        {chartSeries.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            {t(`${prefix}.usage_chart_empty`, "No usage events found")}
          </div>
        ) : (
          <svg viewBox="0 0 600 160" className="w-full h-40" role="img">
            <defs>
              <linearGradient id={`${prefix}-usageArea`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity="0.4" />
                <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity="0" />
              </linearGradient>
            </defs>
            <polygon points={chartArea} fill={`url(#${prefix}-usageArea)`} />
            <polyline
              points={chartPoints}
              fill="none"
              stroke="hsl(var(--primary))"
              strokeWidth="3"
            />
          </svg>
        )}
      </div>
      <div className="flex flex-wrap gap-3 text-sm text-muted-foreground">
        <div>
          {t(`${prefix}.total_cost`, "Total cost")}:{" "}
          <span className="text-foreground font-medium">
            ${totals.totalCost.toFixed(2)}
          </span>
        </div>
        <div>
          {t(`${prefix}.total_tokens`, "Total tokens")}:{" "}
          <span className="text-foreground font-medium">
            {totals.totalTokens.toLocaleString()}
          </span>
        </div>
      </div>
    </div>
  );
};

export default UsageChart;
